import { useEntitiesFromSearch } from 'api/search'
import { useEffect } from 'react'
import { isVersionableDeal, getAgreementFamily } from 'utils/agreementVersioning'

export const usePrefillDealForm = ({
  prefillData,
  organizations,
  contacts,
  handleSelectOrganization,
  setSelectedContacts,
  setNewDeal,
  setContactIds,
  cloneMode,
}) => {
  const { deals: allDeals } = useEntitiesFromSearch()

  useEffect(() => {
    if (!prefillData || !organizations?.length) return

    const {
      deal_uuid,
      id,
      name,
      dba,
      type,
      contacts: contactIdsFromPrefill = [],
      primaryContact = [],
      organizations: orgIds = [],
      startDate,
      endDate,
      agreementOption,
      invoiceOption,
      stage,
      probability,
      recipients,
      owner,
      contractId,
      products,
      descendantId,
      masterAgreementId: existingMasterAgreementId,
      semanticVersion: existingSemanticVersion,
      ...rest
    } = prefillData

    const orgId = Array.isArray(orgIds) ? orgIds[0] : orgIds
    const foundOrg = organizations.find(org => org.id === orgId || org.org_uuid === orgId)

    if (foundOrg) {
      handleSelectOrganization(foundOrg)
    }

    const cloneSymbol = `🧬`
    const replaceSymbol = '⇄'

    const allContactIds = [...contactIdsFromPrefill, ...primaryContact]
    setContactIds(allContactIds) // Set for future use/fetch
    // const formattedName = name?.includes(cloneSymbol)
    //   ? name?.split(cloneSymbol)?.[0]
    //   : name?.includes(replaceSymbol)
    //   ? name?.split(replaceSymbol)?.[0]
    //   : name
    const formattedName = name?.split('-')?.[0]
    // Filter full contact objects from the contacts array
    const matchedContacts = contacts?.filter(contact => allContactIds.includes(contact.contacts_uuid))

    setSelectedContacts(matchedContacts)
    // Compute master agreement versioning for replacements (RMSA/RCSA creation)
    let newMasterAgreementId = ''
    let newSemanticVersion = ''

    if (cloneMode === 'replace' && isVersionableDeal(prefillData)) {
      // Use existing masterAgreementId or fall back to predecessor's deal_uuid
      newMasterAgreementId = existingMasterAgreementId || deal_uuid

      // Count existing versions in the chain to determine next version number
      let versionCount = 1 // start at 1 (the root is version 0 -> "1.0.0")
      if (allDeals?.length) {
        const chainDeals = allDeals.filter(
          (d: any) => d?.masterAgreementId === newMasterAgreementId || d?.deal_uuid === newMasterAgreementId
        )
        // Walk the predecessor chain to count versions
        let current = prefillData
        while (current) {
          versionCount++
          if (current.predecessorId) {
            current = allDeals.find((d: any) => d?.deal_uuid === current.predecessorId)
          } else {
            break
          }
        }
        // If chain count is higher, use that instead
        if (chainDeals.length >= versionCount) {
          versionCount = chainDeals.length
        }
      }
      newSemanticVersion = `1.${versionCount}.0`
    }

    setNewDeal(prev => ({
      // ...prev,
      ...rest,
      // name: cloneMode === 'clone' ? `${formattedName} - ${type} 🧬` : `${formattedName} - ${type} ⇄`,
      name: cloneMode === 'clone' ? `${formattedName} - ${type}` : `${formattedName} - ${type}`,
      dba,
      type,
      contacts: allContactIds,
      organizations: [foundOrg?.id || foundOrg?.org_uuid || orgId],
      startDate,
      endDate,
      agreementOption:
        cloneMode === 'clone'
          ? 'New/Add-on Business'
          : cloneMode === 'replace'
          ? 'Replace Existing Business'
          : agreementOption,
      invoiceOption:
        cloneMode === 'clone'
          ? 'New Invoice'
          : cloneMode === 'replace'
          ? 'Clone & Replace Existing Invoice'
          : invoiceOption,
      invoiceStatus: 'Not Invoiced',
      stage: 'Proposal',
      status: 'Draft',
      dealStatus: products?.length > 0 ? 'In Progress' : 'Draft',
      probability,
      recipients,
      primaryContact,
      owner,
      oldContractId: contractId,
      products: products || [],
      isPredecessor: false,
      isSuccessor: cloneMode === 'replace' ? true : false,
      predecessorId: cloneMode === 'replace' ? deal_uuid : '',
      clonedFromId: cloneMode === 'clone' ? deal_uuid : '',
      isClone: cloneMode === 'clone' ? true : false,
      cloneType: cloneMode === 'clone' ? 'Clone' : 'Replace',
      // Master Agreement versioning
      ...(newMasterAgreementId && { masterAgreementId: newMasterAgreementId }),
      ...(newSemanticVersion && { semanticVersion: newSemanticVersion }),
    }))
  }, [prefillData, organizations])
}
